/**
 * AppShell — the authenticated chrome around every funnel route.
 *
 * Deliberately minimal: a thin header and an <Outlet>. The workspace's own
 * left rail lives in `pages/listiclebuilders/ListicleBuildersLayout`, so this
 * shell only owns the things that must exist above every route — branding
 * slot, the signed-in user, and sign-out.
 *
 * The top bar is the Dashboard's Funnel OS bar on EVERY route (Ludo
 * 2026-09-30, ~/briefs/CODEX-CRM-ONE-TOPBAR.md). Its values live in ONE
 * shared stylesheet, `lbTopBarMinimal.css`, imported here so it loads for
 * every route; this file renders the same markup everywhere (the CSS hides
 * the decorative icons and draws the 6px dot). Page CSS must not style the
 * shell header or the page title — same rule as the ONE left menu (#107).
 */
import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Radio } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import "./lbTopBarMinimal.css";

const PRODUCT_NAME = "Funnel OS";
const HOME = "/app/production/listicle-builder";

// `end` on Dashboard only: every other section lives under HOME, so without it
// Dashboard would read as current on every page.
const TOP_NAV = [
  { to: HOME, label: "Dashboard", end: true },
  { to: `${HOME}/websites`, label: "Funnels" },
  { to: `${HOME}/orders`, label: "Orders" },
  { to: `${HOME}/analytics`, label: "Analytics" },
  { to: `${HOME}/live`, label: "Live" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const signOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex h-screen flex-col bg-background text-foreground" data-testid="app-shell">
      {/* 375px: the header never widens the page. The nav scrolls inside its own
          box (min-w-0 + overflow-x-auto) and the e-mail hides below `sm`; the
          height, padding and gap come from lbTopBarMinimal.css. */}
      <header className="flex min-w-0 shrink-0 items-center border-b border-[var(--mb-color-line-divider)]">
        <Link to={HOME} className="flex shrink-0 items-center gap-2 font-medium tracking-tight" data-testid="app-home-link">
          <Radio className="h-4 w-4 text-[var(--mb-color-text-muted)]" strokeWidth={2} />
          <span className="text-body text-[var(--mb-color-text)]">{PRODUCT_NAME}</span>
        </Link>

        {/* Top-nav tabs, LOCKED option 18a: .mb-nav-item (index.css). NavLink marks the current
            section with aria-current="page": white text on the dark pill, never gold. */}
        <nav className="ml-4 flex min-w-0 items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="app-nav">
          {TOP_NAV.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} className="mb-nav-item shrink-0" data-testid={`app-nav-${it.label.toLowerCase()}`}>
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex shrink-0 items-center">
          {user ? (
            <span className="hidden font-mono text-label text-[var(--mb-color-text-subtle)] sm:inline" data-testid="app-user">
              {user.email}
            </span>
          ) : null}
          <button
            type="button"
            onClick={signOut}
            data-testid="app-signout"
            aria-label="Sign out"
            className="mb-btn mb-btn-ghost"
          >
            <LogOut className="h-4 w-4" strokeWidth={2} />
            <span>Sign out</span>
          </button>
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto" data-testid="app-main">
        <Outlet />
      </main>
    </div>
  );
}
